import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { Button } from "@/components/ui/button";
import type { CameFrom } from "@/features/brain/file-link";
import { useProjectAskedLinks } from "@/features/brain/links-api";
import { useDocuments, useImports } from "@/features/project/documents-api";
import { formatDayOf } from "@/lib/format";
import { AskedLinksList, BackToBrain, Flags, Nothing, Section, Versions } from "./import-review-screen";
import { documentsWord, files, projectReviewFacts } from "./review-facts";
import { projectReviewPath, reviewPath, useReviewImport } from "./review-api";

/**
 * Every question of the project on one screen (2026-10-03, the owner): what
 * the banner's "Review Documents" opens.
 *
 * The same sections as an import's review, over every import at once:
 * versions, links, and the files the check flagged or asked about. What is
 * only about one import stays on that import's screen, linked from the end:
 * what arrived, its log of changes, and its debug panels.
 *
 * "Mark reviewed" marks every import nobody has marked yet. As on an import's
 * screen it does not wait for the questions: a question left open keeps its
 * file counted in the banner until someone answers it.
 */
export function ProjectReviewScreen() {
  const { projectId } = useProjectContext();
  const imports = useImports(projectId);
  const documents = useDocuments(projectId);
  const asked = useProjectAskedLinks(projectId);

  if (imports.isPending || documents.isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Opening the review…</span>
        </div>
      </ContentColumn>
    );
  }

  if (imports.isError || documents.isError) {
    const error = imports.error ?? documents.error;
    return (
      <ContentColumn>
        <BackToBrain projectId={projectId} />
        <div className="mt-4 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">Couldn’t open the review.</p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">{error?.message}</p>
        </div>
      </ContentColumn>
    );
  }

  const facts = projectReviewFacts(imports.data, documents.data, asked.data ?? []);
  const from: CameFrom = { path: projectReviewPath(projectId), label: "Back to the review" };

  return (
    <ContentColumn tight>
      <BackToBrain projectId={projectId} />
      <header className="mt-4 mb-2">
        <p className="font-display text-[0.7rem] font-semibold tracking-[0.12em] text-brand uppercase">Review</p>
        <h1 className="mt-1 font-display text-[1.55rem] leading-[1.1] font-bold tracking-[0.01em] text-ink uppercase">
          Documents to review
        </h1>
        <p className="mt-1.5 text-[0.88rem] text-ink-slate">
          {facts.awaiting === 0 ? "Nothing is waiting." : `${documentsWord(facts.awaiting)} awaiting.`}
        </p>
      </header>

      <Section title="Versions">
        <Versions projectId={projectId} facts={{ asking: facts.asking, placed: [] }} from={from} />
      </Section>

      <Section title="Links to confirm">
        <AskedLinksList projectId={projectId} asked={asked} from={from} />
      </Section>

      <Section title="Flagged files">
        <Flags projectId={projectId} facts={{ hidden: facts.hidden, unsure: facts.unsure, kept: [] }} from={from} />
      </Section>

      <Section title="Imports">
        {facts.waiting.length === 0 ? (
          <Nothing>Every import has been reviewed.</Nothing>
        ) : (
          <Imports projectId={projectId} waiting={facts.waiting} />
        )}
      </Section>
    </ContentColumn>
  );
}

/** The imports nobody has marked reviewed, each linked, and one button for all. */
function Imports({
  projectId,
  waiting,
}: {
  projectId: string;
  waiting: ReturnType<typeof projectReviewFacts>["waiting"];
}) {
  const review = useReviewImport(projectId);
  const [busy, setBusy] = useState(false);

  // One after another: each is its own request, and a failure stops the rest
  // with the reason said, rather than leaving some marked and none said.
  const markAll = async () => {
    setBusy(true);
    try {
      for (const bundle of waiting) await review.mutateAsync(bundle.id);
    } catch {
      // Said below from `review.error`.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-1">
        {waiting.map((bundle) => (
          <li key={bundle.id} className="text-[0.85rem] text-ink">
            <Link
              to={reviewPath(projectId, bundle.id)}
              className="inline-flex items-center gap-1.5 font-semibold text-brand hover:text-brand-hover"
            >
              {files(bundle.files)} arrived on {formatDayOf(bundle.openedAt)}
              <ArrowRight size={14} aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
      <div>
        <Button className="hover:bg-brand-deep" disabled={busy} onClick={() => void markAll()}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          Mark reviewed
        </Button>
        <p className="mt-2 text-[0.82rem] text-ink-slate">
          Marks {waiting.length === 1 ? "this import" : `these ${waiting.length} imports`} reviewed. A question left
          open stays above, and its file stays counted, until someone answers it.
        </p>
        {review.error && (
          <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
            {review.error.message}
          </p>
        )}
      </div>
    </div>
  );
}
